import React, { useEffect, useState } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Alert,
  Badge,
  Container,
} from "reactstrap"
import "react-toastify/dist/ReactToastify.css"

const Facebook = () => {
  const [currentStep, setCurrentStep] = useState(1)
  const [completedSteps, setCompletedSteps] = useState([])

  useEffect(() => {}, [])

  const steps = [
    {
      id: 1,
      title: "Create Google Sheets",
      description: "Set up your Google Sheet to receive Facebook leads",
      icon: "📊",
      color: "primary",
      isCompleted: completedSteps.includes(1),
    },
    {
      id: 2,
      title: "Access Facebook Business Manager",
      description: "Navigate to Facebook Instant Forms",
      icon: "🔗",
      color: "info",
      isCompleted: completedSteps.includes(2),
    },
    {
      id: 3,
      title: "Select Google Sheets as CRM",
      description: "Choose Google Sheets from the CRM dropdown",
      icon: "⚙️",
      color: "warning",
      isCompleted: completedSteps.includes(3),
    },
    {
      id: 4,
      title: "Authenticate Google Account",
      description: "Connect your Google account with Facebook",
      icon: "🔐",
      color: "secondary",
      isCompleted: completedSteps.includes(4),
    },
    {
      id: 5,
      title: "Connect Sheet & Select Form",
      description: "Paste Google Sheet URL and select your form",
      icon: "🔄",
      color: "dark",
      isCompleted: completedSteps.includes(5),
    },
    {
      id: 6,
      title: "Setup Apps Script",
      description: "Add integration script to sync leads with KlikCRM",
      icon: "📝",
      color: "success",
      isCompleted: completedSteps.includes(6),
    },
  ]

  const markStepComplete = stepId => {
    if (!completedSteps.includes(stepId)) {
      setCompletedSteps([...completedSteps, stepId])
    }
  }

  const appScript = `/**
 * Facebook Lead Ads to Webhook Integration Script
 * Simple script to send lead data to external webhook
 */

function onEdit(e) {
  var sheet = e.source.getActiveSheet();
  var row = sheet.getLastRow();      

  // Skip the header row
  if (row === 1) return;

  var headers = sheet.getRange(1, 1, 1, sheet.getLastColumn()).getValues()[0];
  var rowData = sheet.getRange(row, 1, 1, sheet.getLastColumn()).getValues()[0];

  var POST_URL = "https://klikcrm.com/api/${localStorage.getItem(
    "company_name"
  )}/webhook/facebook"; 

  // Build form data according to headers
  var formData = {
    full_name: rowData[headers.indexOf("full name")],
    email: rowData[headers.indexOf("email")],
    city: rowData[headers.indexOf("city")],
    state: rowData[headers.indexOf("state")],
    phone: rowData[headers.indexOf("phone")]
  };

  var options = {
    method: "post",
    contentType: "application/json",
    payload: JSON.stringify(formData)
  };

  UrlFetchApp.fetch(POST_URL, options);
}`

  const copyToClipboard = text => {
    navigator.clipboard.writeText(text)
    alert("Apps Script code copied to clipboard!")
  }

  const renderStepContent = step => {
    switch (step.id) {
      case 1:
        return (
          <Card className="mb-3 shadow-sm border-0">
            <CardBody className="p-4">
              <div className="d-flex align-items-center mb-4">
                <div className="step-icon-wrapper me-3">
                  <span className="step-icon">📊</span>
                </div>
                <div>
                  <h4 className="text-primary mb-1">
                    Step 1: Create Google Sheets
                  </h4>
                  <p className="text-muted mb-0">
                    Set up your Google Sheet to receive Facebook leads
                  </p>
                </div>
              </div>

              <div className="step-content">
                <div className="mb-4">
                  <div className="d-flex align-items-center mb-3">
                    <Badge color="primary" className="me-2">
                      1.1
                    </Badge>
                    <h6 className="mb-0">Create New Spreadsheet</h6>
                  </div>
                  <div className="ps-4">
                    <div className="instruction-item d-flex align-items-start mb-2">
                      <span className="bullet-point me-2">•</span>
                      <span>
                        Go to{" "}
                        <a
                          href="https://sheets.google.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none fw-bold"
                        >
                          sheets.google.com
                        </a>
                      </span>
                    </div>
                    <div className="instruction-item d-flex align-items-start mb-2">
                      <span className="bullet-point me-2">•</span>
                      <span>
                        Click on{" "}
                        <span className="bg-light px-2 py-1 fw-bold">
                          "+ Blank"
                        </span>{" "}
                        to create new sheet
                      </span>
                    </div>
                    <div className="instruction-item d-flex align-items-start mb-2">
                      <span className="bullet-point me-2">•</span>
                      <span>
                        Name it: <code>"Facebook Leads - [Campaign Name]"</code>
                      </span>
                    </div>
                  </div>
                </div>

                <Alert color="warning" className="border-0 shadow-sm">
                  <div className="d-flex align-items-start">
                    <span className="fs-4 me-3">🔒</span>
                    <div>
                      <h6 className="alert-heading mb-2">
                        IMPORTANT - Sharing Settings
                      </h6>
                      <ol className="mb-0">
                        <li>
                          Click{" "}
                          <span className="fw-bold text-warning">"Share"</span>{" "}
                          button (top-right)
                        </li>
                        <li>
                          Change to{" "}
                          <span className="fw-bold text-warning">
                            "Anyone with the link"
                          </span>
                        </li>
                        <li>
                          Set permission to{" "}
                          <span className="fw-bold text-warning">"Editor"</span>
                        </li>
                        <li>
                          <span className="fw-bold text-warning">
                            Copy the sharing URL
                          </span>{" "}
                          - you'll need this for Step 5!
                        </li>
                      </ol>
                    </div>
                  </div>
                </Alert>
              </div>
            </CardBody>
          </Card>
        )

      case 2:
        return (
          <Card className="mb-3">
            <CardBody>
              <h5 className="text-primary">
                Step 2: Access Facebook Business Manager
              </h5>

              <div className="mb-3">
                <h6>2.1 Navigate to Instant Forms:</h6>
                <ol>
                  <li>
                    Open{" "}
                    <a
                      href="https://business.facebook.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Facebook Business Manager
                    </a>
                  </li>
                  <li>
                    Go to:{" "}
                    <a
                      href="https://business.facebook.com/latest/instant_forms/forms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      https://business.facebook.com/latest/instant_forms/forms
                    </a>
                  </li>
                  <li>You'll see a list of your lead forms</li>
                </ol>
              </div>

              <div className="mb-3">
                <h6>2.2 Select Your Lead Form:</h6>
                <ol>
                  <li>Find the lead form you want to connect</li>
                  <li>
                    <strong>Click on the form name</strong> to open details
                  </li>
                  <li>
                    Look for and click on the <strong>"CRM setup"</strong> tab
                  </li>
                </ol>
              </div>

              <Alert color="info">
                <strong>💡 Tip:</strong> If you don't see any forms, make sure
                you have active Facebook Lead Ad campaigns running.
              </Alert>
            </CardBody>
          </Card>
        )

      case 3:
        return (
          <Card className="mb-3">
            <CardBody>
              <h5 className="text-primary">
                Step 3: Select Google Sheets as CRM System
              </h5>

              <div className="mb-3">
                <h6>3.1 Choose CRM Integration:</h6>
                <ol>
                  <li>
                    In the CRM setup tab, find{" "}
                    <strong>"Select a CRM system"</strong> dropdown
                  </li>
                  <li>Click on the dropdown menu</li>
                  <li>
                    Select <strong>"Google Sheets"</strong> from the list
                  </li>
                  <li>
                    Click <strong>"Continue"</strong> to proceed
                  </li>
                </ol>
              </div>

              <div className="mb-3">
                <h6>3.2 Integration Benefits:</h6>
                <ul className="list-unstyled">
                  <li>✅ Automatic lead capture</li>
                  <li>✅ Real-time synchronization</li>
                  <li>✅ No manual data entry required</li>
                  <li>✅ Direct integration with your workflow</li>
                </ul>
              </div>
            </CardBody>
          </Card>
        )

      case 4:
        return (
          <Card className="mb-3">
            <CardBody>
              <h5 className="text-primary">
                Step 4: Authenticate with Your Google Account
              </h5>

              <div className="mb-3">
                <h6>4.1 Google Authentication Process:</h6>
                <ol>
                  <li>
                    Click <strong>"Connect to Google"</strong> or similar button
                  </li>
                  <li>A popup window will appear asking you to sign in</li>
                  <li>
                    <strong>Sign in</strong> with the Google account that owns
                    your spreadsheet
                  </li>
                  <li>
                    <strong>Grant permissions</strong> when Facebook requests
                    to:
                    <ul>
                      <li>Access your Google Drive</li>
                      <li>Read and edit your Google Sheets</li>
                      <li>Create and modify spreadsheets</li>
                    </ul>
                  </li>
                  <li>
                    Click <strong>"Allow"</strong> or{" "}
                    <strong>"Grant Access"</strong>
                  </li>
                </ol>
              </div>

              <div className="mb-3">
                <h6>4.2 Verify Connection:</h6>
                <ul>
                  <li>You should see a confirmation message</li>
                  <li>Your Google account email will be displayed</li>
                  <li>
                    Status should show as "Connected" or "Authentication
                    Successful"
                  </li>
                </ul>
              </div>

              <Alert color="warning">
                <strong>⚠️ Important:</strong> Make sure you're using the same
                Google account that owns the spreadsheet you created in Step 1.
              </Alert>
            </CardBody>
          </Card>
        )

      case 5:
        return (
          <Card className="mb-3">
            <CardBody>
              <h5 className="text-primary">
                Step 5: Copy and Paste Google Sheet URL & Select Form
              </h5>

              <div className="mb-3">
                <h6>5.1 Paste Google Sheet URL:</h6>
                <ol>
                  <li>
                    Find the <strong>"Google Sheet URL"</strong> input field
                  </li>
                  <li>
                    <strong>Paste the sharing URL</strong> you copied from Step
                    1
                  </li>
                  <li>
                    URL should look like:{" "}
                    <code>
                      https://docs.google.com/spreadsheets/d/[SHEET_ID]/edit...
                    </code>
                  </li>
                  <li>Facebook will verify it can access your sheet</li>
                </ol>
              </div>

              <div className="mb-3">
                <h6>5.2 Select Lead Form & Map Fields:</h6>
                <ol>
                  <li>
                    From <strong>"Select form"</strong> dropdown, choose your
                    Facebook lead form
                  </li>
                  <li>
                    <strong>Map fields</strong> from Facebook form to Google
                    Sheet columns:
                  </li>
                </ol>
                <div className="table-responsive">
                  <table className="table table-bordered table-sm">
                    <thead>
                      <tr>
                        <th>Facebook Field</th>
                        <th>→</th>
                        <th>Google Sheet Column</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Full Name</td>
                        <td>→</td>
                        <td>Name (Column A)</td>
                      </tr>
                      <tr>
                        <td>Email</td>
                        <td>→</td>
                        <td>Email (Column B)</td>
                      </tr>
                      <tr>
                        <td>Phone</td>
                        <td>→</td>
                        <td>Phone (Column C)</td>
                      </tr>
                      <tr>
                        <td>Custom Fields</td>
                        <td>→</td>
                        <td>Map to appropriate columns</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mb-3">
                <h6>5.3 Test Connection:</h6>
                <ol>
                  <li>
                    Click <strong>"Test Connection"</strong> to verify
                    everything works
                  </li>
                  <li>A test entry might be added to your Google Sheet</li>
                  <li>You should see a success message</li>
                </ol>
              </div>
            </CardBody>
          </Card>
        )

      case 6:
        return (
          <Card className="mb-3">
            <CardBody>
              <h5 className="text-primary">
                Step 6: Setup Apps Script for KlikCRM Integration
              </h5>

              <div className="mb-3">
                <h6>6.1 Access Google Apps Script:</h6>
                <ol>
                  <li>Open your Google Sheet</li>
                  <li>
                    Go to <strong>Extensions → Apps Script</strong>
                  </li>
                  <li>
                    A new tab will open with the Google Apps Script editor
                  </li>
                </ol>
              </div>

              <div className="mb-3">
                <h6>6.2 Add Integration Script:</h6>
                <ol>
                  <li>Delete any existing code in the editor</li>
                  <li>Click the button below to copy the integration script</li>
                  <li>Paste the script into the Apps Script editor</li>
                  <li>
                    Update the <code>POST_URL</code> variable with your actual
                    webhook URL
                  </li>
                </ol>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold mb-0">Integration Script</h6>
                  <Button
                    color="success"
                    size="sm"
                    onClick={() => copyToClipboard(appScript)}
                    className="d-flex align-items-center"
                  >
                    <span className="me-2">📋</span>
                    Copy to Clipboard
                  </Button>
                </div>

                <div className="position-relative mb-3">
                  <pre
                    className="bg-dark text-light p-4"
                    style={{
                      maxHeight: "400px",
                      overflow: "auto",
                      fontSize: "12px",
                      lineHeight: "1.4",
                      borderRadius: "8px",
                    }}
                  >
                    <code>{appScript}</code>
                  </pre>
                </div>

                <Alert color="warning" className="border-0">
                  <div className="d-flex align-items-start">
                    <span className="me-3" style={{ fontSize: "20px" }}>
                      ⚠️
                    </span>
                    <div>
                      <strong>Important:</strong> Remember to update the{" "}
                      <code>POST_URL</code> variable with your actual webhook
                      URL before saving the script.
                    </div>
                  </div>
                </Alert>
              </div>

              <div className="mb-3">
                <h6>6.3 Setup Trigger:</h6>
                <ol>
                  <li>
                    In Apps Script, click the <strong>clock icon</strong>{" "}
                    (Triggers) in sidebar
                  </li>
                  <li>
                    Click <strong>"+ Add Trigger"</strong>
                  </li>
                  <li>
                    Configure:
                    <ul>
                      <li>
                        <strong>Function:</strong> onEdit
                      </li>
                      <li>
                        <strong>Event source:</strong> From spreadsheet
                      </li>
                      <li>
                        <strong>Event type:</strong> On edit
                      </li>
                    </ul>
                  </li>
                  <li>
                    Click <strong>"Save"</strong>
                  </li>
                  <li>Authorize the script when prompted</li>
                </ol>
              </div>

              <Alert color="success">
                <h6>🎉 Final Result:</h6>
                <p className="mb-0">
                  <strong>
                    Wait for next lead - it will be fetched to KlikCRM!
                  </strong>
                  <br />
                  Timeline: Facebook form → Google Sheets (2-5 min) → KlikCRM
                  (immediate)
                </p>
              </Alert>
            </CardBody>
          </Card>
        )

      default:
        return null
    }
  }

  return (
    <>
      <style jsx>{`
        .step-icon-wrapper {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
        }
        .step-icon {
          font-size: 24px;
        }
        .instruction-item {
          padding: 8px 0;
          border-left: 3px solid transparent;
        }
        .bullet-point {
          color: #007bff;
          font-weight: bold;
        }
        .feature-card {
          border: 1px solid #e9ecef;
        }
        .step-navigation {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-radius: 15px;
          padding: 20px;
          margin-bottom: 30px;
        }
        .step-card {
          cursor: pointer;
          border: 2px solid transparent;
        }
        .step-card.active {
          border-color: #007bff;
          box-shadow: 0 8px 25px rgba(0, 123, 255, 0.2);
        }
        .progress-modern {
          height: 8px;
          border-radius: 10px;
          background: #e9ecef;
          overflow: hidden;
        }
        .progress-bar-modern {
          background: linear-gradient(90deg, #28a745, #20c997);
          border-radius: 10px;
        }
      `}</style>

      <Container fluid className="px-4 py-5">
        <div className="max-width-1200 mx-auto">
          {/* Header Section */}
          <div className="text-center mb-5">
            <div
              className="d-inline-flex align-items-center justify-content-center mb-4"
              style={{
                width: "80px",
                height: "80px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "20px",
                boxShadow: "0 8px 30px rgba(102, 126, 234, 0.3)",
              }}
            >
              <span style={{ fontSize: "32px" }}>🔗</span>
            </div>
            <h1 className="display-6 fw-bold mb-3" style={{ color: "#2d3748" }}>
              Facebook Lead Ads Integration
            </h1>
            <p
              className="lead text-muted mb-4"
              style={{ maxWidth: "600px", margin: "0 auto" }}
            >
              Connect Facebook Lead Ads → Google Sheets → KlikCRM in 6 simple
              steps
            </p>
          </div>

          {/* Step Navigation */}
          <div className="step-navigation mb-5">
            <h5 className="text-center mb-4 fw-bold">Setup Steps</h5>
            <Row className="g-3">
              {steps.map(step => (
                <Col key={step.id} lg={2} md={4} sm={6}>
                  <Card
                    className={`step-card h-100 ${
                      currentStep === step.id ? "active" : ""
                    }`}
                    onClick={() => setCurrentStep(step.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <CardBody className="text-center p-3">
                      <div className="mb-3">
                        {step.isCompleted ? (
                          <div
                            className="d-inline-flex align-items-center justify-content-center"
                            style={{
                              width: "50px",
                              height: "50px",
                              background:
                                "linear-gradient(135deg, #28a745, #20c997)",
                              borderRadius: "12px",
                              fontSize: "20px",
                            }}
                          >
                            ✓
                          </div>
                        ) : (
                          <div
                            className="d-inline-flex align-items-center justify-content-center"
                            style={{
                              width: "50px",
                              height: "50px",
                              background:
                                currentStep === step.id
                                  ? "linear-gradient(135deg, #007bff, #0056b3)"
                                  : "linear-gradient(135deg, #6c757d, #495057)",
                              borderRadius: "12px",
                              fontSize: "18px",
                            }}
                          >
                            {step.icon}
                          </div>
                        )}
                      </div>
                      <h6 className="small fw-bold mb-2">{step.title}</h6>
                      <small className="text-muted">{step.description}</small>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>

          {/* Step Content */}
          <Row>
            <Col>
              {renderStepContent(steps.find(s => s.id === currentStep))}
            </Col>
          </Row>

          {/* Navigation Buttons */}
          <Card className="border-0 bg-light">
            <CardBody className="p-4">
              <Row className="align-items-center">
                <Col md={4}>
                  <Button
                    color="outline-secondary"
                    size="lg"
                    className="w-100"
                    onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                    disabled={currentStep === 1}
                  >
                    <span className="me-2">←</span>
                    Previous Step
                  </Button>
                </Col>
                <Col md={4} className="text-center">
                  <div className="py-3">
                    <Badge color="primary" pill className="px-4 py-2 fs-6">
                      Step {currentStep} of 6
                    </Badge>
                  </div>
                </Col>
                <Col md={4}>
                  <Button
                    color="primary"
                    size="lg"
                    className="w-100"
                    onClick={() => setCurrentStep(Math.min(6, currentStep + 1))}
                    disabled={currentStep === 6}
                  >
                    Next Step
                    <span className="ms-2">→</span>
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Completion Status */}
          {completedSteps.length === 6 && (
            <Alert color="success" className="mt-5 border-0 shadow-sm">
              <div className="text-center p-4">
                <div className="mb-3">
                  <span style={{ fontSize: "48px" }}>🎉</span>
                </div>
                <h3 className="fw-bold text-success mb-3">
                  Integration Complete!
                </h3>
                <p className="lead mb-4">
                  Your Facebook Lead Ads are now connected to Google Sheets and
                  will automatically sync to KlikCRM.
                </p>
                <Badge color="success" pill className="px-4 py-2">
                  <span className="me-2">✅</span>
                  Ready to receive leads!
                </Badge>
              </div>
            </Alert>
          )}
        </div>
      </Container>
    </>
  )
}

export default Facebook
